import { z } from 'zod';
import { EffectiveAccessSchema, ViewingAsSchema } from './access';
import { PositionKindSchema } from './org';
import { AccountStatusSchema, PersonKindSchema } from './people';

// Requests --------------------------------------------------------------------

export const LoginRequestSchema = z.object({
  /** Work email, staff number or (for imported accounts without an email) the old username. */
  identifier: z.string().trim().min(1).max(255),
  password: z.string().min(1).max(200),
  /** "Keep me signed in": 30 days idle / 90 days absolute instead of 12 hours / 7 days. */
  remember: z.boolean().default(false),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const PasswordChangeRequestSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: z.string().min(1).max(200),
});
export type PasswordChangeRequest = z.infer<typeof PasswordChangeRequestSchema>;

// Resets (M5, spec §3.4) ------------------------------------------------------

export const ForgotPasswordRequestSchema = z.object({
  /** Work email or staff number (any sign-in identifier works). */
  identifier: z.string().trim().min(1).max(255),
});
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequestSchema>;

export const ResetPasswordRequestSchema = z.object({
  /** From the emailed link: 43 base64url characters. */
  token: z.string().trim().min(1).max(200),
  newPassword: z.string().min(1).max(200),
});
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;

export const RESET_CODE_LENGTH = 6;

export const RedeemCodeRequestSchema = z.object({
  /** Staff number (or any sign-in identifier). */
  identifier: z.string().trim().min(1).max(255),
  /** The 6-character code the manager read out; case and spaces don't matter. */
  code: z.string().trim().min(1).max(20),
  newPassword: z.string().min(1).max(200),
});
export type RedeemCodeRequest = z.infer<typeof RedeemCodeRequestSchema>;

// Errors ----------------------------------------------------------------------

/** Every auth failure is JSON with one of these codes; the web shows copy per code. */
export const AuthErrorCodeSchema = z.enum([
  'bad_credentials',
  'locked',
  'disabled',
  'rate_limited',
  'unauthenticated',
  'forbidden_origin',
  'forbidden',
  'read_only_view_as',
  'bad_current_password',
  'password_too_short',
  'password_too_long',
  'password_breached',
  'not_implemented',
  /** M5 */
  'invalid_token',
  'bad_code',
  'off_site',
  'not_in_reporting_line',
  'no_account',
  'no_verified_email',
  'not_found',
]);
export type AuthErrorCode = z.infer<typeof AuthErrorCodeSchema>;

export const AuthErrorSchema = z.object({
  error: AuthErrorCodeSchema,
  message: z.string(),
  /** Locked accounts: when the lock lifts (null = needs a reset or an admin unlock). */
  lockedUntil: z.iso.datetime().nullable().optional(),
  minutesRemaining: z.number().int().nullable().optional(),
});
export type AuthError = z.infer<typeof AuthErrorSchema>;

// Session ---------------------------------------------------------------------

export const MeSchema = z.object({
  personId: z.uuid(),
  accountId: z.uuid(),
  givenName: z.string(),
  familyName: z.string(),
  preferredName: z.string().nullable(),
  /** "Preferred Family" as shown in the shell. */
  displayName: z.string(),
  initials: z.string(),
  kind: PersonKindSchema,
  staffNumber: z.string().nullable(),
  jobTitle: z.string().nullable(),
  homeSite: z.object({ code: z.string(), name: z.string() }).nullable(),
  positions: z.array(
    z.object({ title: z.string(), kind: PositionKindSchema, scopeName: z.string() }),
  ),
  /** The person holding the line-manager seat, when known. */
  lineManager: z.object({ title: z.string(), name: z.string().nullable() }).nullable(),
  account: z.object({
    status: AccountStatusSchema,
    loginEmail: z.string().nullable(),
    mustChangePassword: z.boolean(),
    lastLoginAt: z.iso.datetime().nullable(),
    passwordChangedAt: z.iso.datetime().nullable(),
  }),
});
export type Me = z.infer<typeof MeSchema>;

export const SessionResponseSchema = z.object({
  /** The signed-in person (the actor, even while viewing as someone else). */
  me: MeSchema,
  /** Effective access of the subject: `me`, or `viewingAs` while View as is on (D10). */
  access: EffectiveAccessSchema,
  /** Set while the session is viewing as another person: every request is read-only. */
  viewingAs: ViewingAsSchema.nullable(),
  session: z.object({
    id: z.string(),
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    remember: z.boolean(),
    /** Started on a shared-PC site network: 2h idle, never remembered (spec §3.5). */
    sharedPc: z.boolean().optional(),
  }),
  /** Sign-in only: "Keep me signed in" was ticked but this is a shared-PC site. */
  rememberIgnored: z.boolean().optional(),
  /**
   * Fleet M2 (D65): whether Fleet is relevant to the subject (a vehicle, a licence check or
   * consent, a grey-fleet car or declaration, a fine, or a fleet/manager permission). The nav
   * hides Fleet when false; absent means "decide from permissions alone".
   */
  fleetRelevant: z.boolean().optional(),
});
export type SessionResponse = z.infer<typeof SessionResponseSchema>;

export const SessionListItemSchema = z.object({
  id: z.string(),
  current: z.boolean(),
  createdAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  ip: z.string().nullable(),
  deviceLabel: z.string().nullable(),
  remember: z.boolean(),
  sharedPc: z.boolean().optional(),
});
export type SessionListItem = z.infer<typeof SessionListItemSchema>;

export const SessionListResponseSchema = z.object({
  sessions: z.array(SessionListItemSchema),
});
export type SessionListResponse = z.infer<typeof SessionListResponseSchema>;

export const OkResponseSchema = z.object({
  ok: z.literal(true),
  /** Sessions ended by this action (sign out everywhere, password change). */
  revoked: z.number().int().optional(),
});
export type OkResponse = z.infer<typeof OkResponseSchema>;
